import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import photo from '../../assets/images/profile-photo-base.png';
import {
  B17ImportProfileData,
  B17ImportSuggestion,
  CatalogDictItem,
  CatalogGenderItem,
  CatalogCurrencyItem,
  CatalogLanguageItem,
  CatalogProfile,
  useDeleteCatalogProfilePhotoMutation,
  useGetCatalogProfileDictionariesQuery,
  useGetCatalogProfileMeQuery,
  useGetCatalogProfilePhotoQuery,
  useImportCatalogProfileB17Mutation,
  useSubmitCatalogProfileMutation,
  useUnpublishCatalogProfileMutation,
  useUpdateCatalogProfileMeMutation,
  useUploadCatalogProfilePhotoMutation,
  useWithdrawCatalogProfileMutation,
} from '../../services/catalogProfile.api';
import { useAppSelector } from '../../hooks/redux';
import { usePosthogCapture } from '../../hooks/usePosthogCapture';
import { selectUser } from '../../store/auth.slice';
import {
  Input,
  Loader,
  ConfirmDialog,
  CustomSelect,
  PhoneInputWithMask,
  RichTextEditor,
  Card,
  Heading,
  Button,
  TextButton,
  Pressable,
  Checkbox,
  MultiSelect,
  CloseButton,
  TextLink,
  useFilePicker,
} from '../../shared/ui';
import { catalogProfilePublicUrl } from '../../configs/api/api.config';

const publishedDict = (items: CatalogDictItem[] = []) => items.filter((i) => i.status === 'published');
const publishedLang = (items: CatalogLanguageItem[] = []) => items.filter((i) => i.status === 'published');
const publishedGender = (items: CatalogGenderItem[] = []) => items.filter((i) => i.status === 'published');
const publishedCurrency = (items: CatalogCurrencyItem[] = []) => items.filter((i) => i.status === 'published');

const dictToMultiItems = (items: CatalogDictItem[]) => publishedDict(items).map((i) => ({ id: i.id, name: i.title }));

const langToMultiItems = (items: CatalogLanguageItem[]) =>
  publishedLang(items).map((i) => ({ id: i.code, name: i.name }));

const currencyLabel = (c: CatalogCurrencyItem) => c.long_name;

const REQUIRED_FIELD_MESSAGE = 'Обязательное поле';
const CONTACT_REQUIRED_MESSAGE = 'Укажите хотя бы один способ связи: телефон, Telegram или WhatsApp';

const ALLOWED_PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;

const B17_IMPORT_SCALAR_FIELDS = [
  'first_name',
  'last_name',
  'middle_name',
  'title',
  'about_me',
  'education_description',
  'city',
  'experience_years',
  'price',
  'duration_minutes',
] as const satisfies readonly (keyof B17ImportProfileData)[];

const B17_IMPORT_M2M_FIELDS = [
  ['suggested_modalities', 'modalities'],
  ['suggested_issues', 'issues'],
  ['suggested_specialization', 'specialization'],
] as const;

const B17_IMPORT_SECTION_LABELS: Record<string, string> = {
  suggested_modalities: 'Методы лечения',
  suggested_issues: 'Запросы',
  suggested_specialization: 'Специализация',
};

const B17_CONFIDENCE_FIELD_LABELS: Record<string, string> = {
  first_name: 'Имя',
  last_name: 'Фамилия',
  middle_name: 'Отчество',
  title: 'Заголовок',
  about_me: 'Обо мне',
  education_description: 'Образование',
  city: 'Город',
  experience_years: 'Стаж',
  price: 'Стоимость',
  duration_minutes: 'Длительность',
};

const B17_LOW_CONFIDENCE_THRESHOLD = 0.5;

const B17_ALLOWED_HOSTS = ['b17.ru', 'www.b17.ru'];
const B17_INVALID_URL_MESSAGE = 'Ссылка должна вести на профиль b17.ru, например https://www.b17.ru/ivanov_ivan';

const isValidB17Url = (value: string): boolean => {
  const normalized = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const parsed = new URL(normalized);
    return parsed.protocol === 'https:' && B17_ALLOWED_HOSTS.includes(parsed.hostname.toLowerCase());
  } catch {
    return false;
  }
};

const requiredWhenPublished = <T extends yup.AnySchema>(schema: T, message = REQUIRED_FIELD_MESSAGE) =>
  schema.when('status', {
    is: (status: string) => status !== 'draft',
    then: (s) => s.required(message),
  });

const catalogProfileSchema = yup
  .object({
    /**
     * Поля без собственных правил, но объявить их обязательно: на них ссылаются
     * .when(...) и тест contact-required, а нужны они там как источник данных,
     * а не как предмет валидации.
     */
    status: yup.string(),
    price_offline: yup.number().nullable(),
    phone_1: yup.string(),
    telegram: yup.string(),
    whatsup: yup.string(),
    last_name: requiredWhenPublished(yup.string().trim()),
    first_name: requiredWhenPublished(yup.string().trim()),
    title: requiredWhenPublished(yup.string().trim()),
    city: requiredWhenPublished(yup.string().trim()),
    specialization: yup
      .array()
      .of(yup.string().required())
      .when('status', {
        is: (status: string) => status !== 'draft',
        then: (s) => s.min(1, 'Выберите хотя бы одну специализацию'),
      }),
    formats: yup
      .array()
      .of(yup.string().required())
      .when('status', {
        is: (status: string) => status !== 'draft',
        then: (s) => s.min(1, 'Выберите хотя бы один формат консультаций'),
      }),
    price: yup
      .number()
      .nullable()
      .when(['status', 'price_offline'], {
        is: (status: string, priceOffline: number | null) => status !== 'draft' && priceOffline == null,
        then: (s) => s.required('Укажите стоимость онлайн или оффлайн консультации'),
      }),
    currency: yup.string().when(['status', 'price', 'price_offline'], {
      is: (status: string, price: number | null, priceOffline: number | null) =>
        status !== 'draft' && (price != null || priceOffline != null),
      then: (s) => s.required('Укажите валюту'),
    }),
    about_me: yup.string().when('status', {
      is: (status: string) => status !== 'draft',
      then: (s) => s.test('not-empty', 'Расскажите о себе', (value) => !!(value || '').replace(/<[^>]*>/g, '').trim()),
    }),
  })
  .test('contact-required', CONTACT_REQUIRED_MESSAGE, function contactRequired(value) {
    if (value?.status === 'draft') return true;
    if (value?.phone_1?.trim() || value?.telegram?.trim() || value?.whatsup?.trim()) return true;
    return this.createError({ path: 'phone_1', message: CONTACT_REQUIRED_MESSAGE });
  });

const emptyProfile: CatalogProfile = {
  status: 'draft',
  has_published_profile: false,
  first_name: '',
  last_name: '',
  middle_name: '',
  title: '',
  age: null,
  city: '',
  gender: '',
  specialization: [],
  experience_years: null,
  profile_photo: null,
  price: null,
  price_offline: null,
  currency: '',
  duration_minutes: null,
  formats: [],
  languages: [],
  religion: '',
  about_me: '',
  education_description: '',
  education_type: '',
  diploma_verified: false,
  modalities: [],
  issues: [],
  working_with: [],
  phone_1: '',
  phone_2: '',
  telegram: '',
  vk: '',
  whatsup: '',
  b17_profile: '',
  work_on_yasno: false,
  work_on_alter: false,
  work_on_meta: false,
  work_on_zigmung: false,
};

const { status: _emptyProfileStatus, ...emptyProfileContent } = emptyProfile;

const CATALOG_STATUS_META: Record<CatalogProfile['status'], { title: string; icon: string; iconClass: string }> = {
  draft: {
    title: 'Черновик',
    icon: 'edit_document',
    iconClass: 'bg-surface-muted text-ink-muted ring-line',
  },
  ready_for_review: {
    title: 'На автоматической проверке',
    icon: 'pending',
    iconClass: 'bg-warning-surface text-warning ring-warning-line',
  },
  manual_review: {
    title: 'На ручной проверке',
    icon: 'assignment_ind',
    iconClass: 'bg-info-surface text-info ring-info-line',
  },
  rejected: {
    title: 'Требуются исправления',
    icon: 'warning',
    iconClass: 'bg-danger-surface text-danger ring-danger-line',
  },
  published: {
    title: 'Опубликовано в каталоге',
    icon: 'verified',
    iconClass: 'bg-success-surface text-success ring-success-line',
  },
};

const normalizeProfile = (data: CatalogProfile): CatalogProfile => {
  const result = { ...data };
  (Object.keys(emptyProfile) as (keyof CatalogProfile)[]).forEach((key) => {
    if (result[key] == null && emptyProfile[key] !== null) {
      (result as any)[key] = emptyProfile[key];
    }
  });
  return result;
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Heading size="sm" className="mb-4">
    {children}
  </Heading>
);

type CatalogProfileTabProps = {
  onDirtyChange?: (isDirty: boolean) => void;
};

export const CatalogProfileTab = ({ onDirtyChange }: CatalogProfileTabProps = {}) => {
  const captureEvent = usePosthogCapture();
  const userData = useAppSelector(selectUser);
  const { data: meResponse, isLoading: isLoadingMe } = useGetCatalogProfileMeQuery(undefined, {
    // Вкладка размонтируется при смене activeTab, поэтому polling существует
    // только пока пользователь просматривает профиль каталога.
    refetchOnMountOrArgChange: true,
    pollingInterval: 60_000,
  });
  const { data: dicts } = useGetCatalogProfileDictionariesQuery();
  const [updateProfile, { isLoading: isSaving }] = useUpdateCatalogProfileMeMutation();
  const [submitProfile, { isLoading: isSubmitting }] = useSubmitCatalogProfileMutation();
  const [withdrawProfile, { isLoading: isWithdrawing }] = useWithdrawCatalogProfileMutation();
  const [unpublishProfile, { isLoading: isUnpublishing }] = useUnpublishCatalogProfileMutation();

  const [photoPreview, setPhotoPreview] = useState('');
  const [photoObjectUrl, setPhotoObjectUrl] = useState('');
  const [uploadPhoto, { isLoading: isUploadingPhoto }] = useUploadCatalogProfilePhotoMutation();
  const [deletePhoto, { isLoading: isDeletingPhoto }] = useDeleteCatalogProfilePhotoMutation();

  const hasStoredPhoto = !!meResponse?.data?.profile_photo;
  const { data: photoBlob } = useGetCatalogProfilePhotoQuery(undefined, { skip: !hasStoredPhoto });

  useEffect(() => {
    if (!photoBlob) {
      setPhotoObjectUrl('');
      return;
    }
    const url = URL.createObjectURL(photoBlob);
    setPhotoObjectUrl(url);
    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    return () => URL.revokeObjectURL(url);
  }, [photoBlob]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CatalogProfile>({
    defaultValues: emptyProfile,
    resolver: yupResolver(catalogProfileSchema) as any,
  });

  const hasInitializedFormRef = useRef(false);

  useEffect(() => {
    if (!meResponse) return;
    const profileData = meResponse.exists && meResponse.data ? normalizeProfile(meResponse.data) : { ...emptyProfile };
    if (!profileData.first_name) profileData.first_name = userData?.first_name || '';
    if (!profileData.last_name) profileData.last_name = userData?.last_name || '';
    if (!profileData.middle_name) profileData.middle_name = userData?.middle_name || '';
    if (!profileData.phone_1) profileData.phone_1 = userData?.phone || '';
    if (!hasInitializedFormRef.current) {
      hasInitializedFormRef.current = true;
      reset(profileData);
    } else {
      setValue('profile_photo', profileData.profile_photo, { shouldDirty: false });
      setValue('status', profileData.status, { shouldDirty: false });
      setValue('has_published_profile', profileData.has_published_profile, { shouldDirty: false });
      setValue('moderation', profileData.moderation, { shouldDirty: false });
    }
  }, [meResponse, reset, setValue, userData]);

  const watchedValues = watch();

  const hasChanges = useCallback(
    (current: CatalogProfile): boolean => {
      if (!meResponse) return false;
      const original = meResponse.exists && meResponse.data ? normalizeProfile(meResponse.data) : emptyProfile;
      return JSON.stringify(current) !== JSON.stringify(original);
    },
    [meResponse],
  );

  const hasContent = useCallback((current: CatalogProfile): boolean => {
    const { status: _status, has_published_profile: _hasPublishedProfile, ...rest } = current;
    return JSON.stringify(rest) !== JSON.stringify(emptyProfileContent);
  }, []);

  const isDirty = hasChanges(watchedValues);
  const isSaveEnabled = isDirty && hasContent(watchedValues) && !isSaving && !isSubmitting;
  const contactError = errors.phone_1?.message;
  const statusMeta = CATALOG_STATUS_META[watchedValues.status];
  const hasPublishedProfile = watchedValues.has_published_profile;
  const publicProfileUrl =
    hasPublishedProfile && meResponse?.publicProfileId
      ? `${catalogProfilePublicUrl.replace(/\/+$/, '')}/${encodeURIComponent(meResponse.publicProfileId)}`
      : null;
  const statusMessage = (() => {
    switch (watchedValues.status) {
      case 'published':
        return 'Если вы внесёте изменения, они появятся в каталоге только после повторной отправки и успешной проверки.';
      case 'ready_for_review':
        return 'Анкета проходит автоматическую проверку. Ничего делать не нужно — дождитесь результата. Предыдущая одобренная версия остаётся в каталоге.';
      case 'manual_review':
        return 'Анкета передана модератору. Ничего делать не нужно — дождитесь решения. Чтобы внести новые правки, сначала отзовите заявку.';
      case 'rejected':
        return watchedValues.moderation?.user_message || 'Исправьте анкету и отправьте её на повторную проверку.';
      default:
        return hasPublishedProfile
          ? 'В каталоге остаётся опубликованная версия. Отправьте изменения на повторную проверку или снимите анкету с публикации.'
          : 'Заполните анкету и отправьте её на публикацию.';
    }
  })();
  // У опубликованной анкеты отправка нужна только после реального изменения формы.
  // Черновик и отклонённую ревизию можно отправить повторно после сохранения.
  const canSendForReview = watchedValues.status !== 'published' || (isDirty && hasContent(watchedValues));

  const [importB17Profile, { isLoading: isImportingB17 }] = useImportCatalogProfileB17Mutation();
  const [importUrlError, setImportUrlError] = useState('');
  const [importSuggestions, setImportSuggestions] = useState<Partial<
    Record<(typeof B17_IMPORT_M2M_FIELDS)[number][0], B17ImportSuggestion[]>
  > | null>(null);
  const [importConfidence, setImportConfidence] = useState<Record<string, number>>({});
  const [importBackup, setImportBackup] = useState<CatalogProfile | null>(null);
  const [pendingImportUrl, setPendingImportUrl] = useState<string | null>(null);

  const applyB17ImportData = useCallback(
    (data: B17ImportProfileData) => {
      setValue('b17_profile', data.b17_profile, { shouldDirty: true });
      B17_IMPORT_SCALAR_FIELDS.forEach((key) => {
        const value = data[key];
        if (value == null) return;
        // about_me and education_description arrive as WYSIWYG HTML from the B17
        // import contract, so Tiptap can load their formatting without escaping it.
        setValue(key, value as any, { shouldDirty: true });
      });

      const matchedIds = (items: B17ImportSuggestion[] = []) =>
        items.filter((item) => item.id && !item.unmatched).map((item) => item.id as string);

      B17_IMPORT_M2M_FIELDS.forEach(([sourceKey, targetKey]) => {
        const suggestions = data[sourceKey];
        if (suggestions?.length) {
          setValue(targetKey, matchedIds(suggestions), { shouldDirty: true });
        }
      });

      setImportSuggestions({
        suggested_modalities: data.suggested_modalities,
        suggested_issues: data.suggested_issues,
        suggested_specialization: data.suggested_specialization,
      });
      setImportConfidence(data.confidence ?? {});
    },
    [setValue],
  );

  const performB17Import = useCallback(
    async (url: string, backup: CatalogProfile) => {
      try {
        const data = await importB17Profile({ url }).unwrap();
        applyB17ImportData(data);
        setImportBackup(backup);
        toast.success('Данные из b17 подставлены в форму — проверьте их и сохраните');
      } catch (err: any) {
        const status = err?.status;
        if (status === 400) {
          setImportUrlError('Некорректная ссылка на профиль b17.ru');
        } else if (status === 404) {
          setImportUrlError('Анкета не найдена или недоступна публично');
        } else if (status === 503) {
          toast.error('Импорт из b17 временно не настроен');
        } else if (status === 504) {
          toast.error('Обработка превысила время ожидания, попробуйте ещё раз');
        } else if (status === 502) {
          toast.error('b17.ru или сервис обработки временно недоступны, попробуйте позже');
        } else {
          toast.error('Не удалось импортировать профиль b17');
        }
      }
    },
    [importB17Profile, applyB17ImportData],
  );

  const onImportB17Click = () => {
    const url = (watchedValues.b17_profile || '').trim();
    if (!url) {
      setImportUrlError('Введите ссылку на профиль b17.ru');
      return;
    }
    if (!isValidB17Url(url)) {
      setImportUrlError(B17_INVALID_URL_MESSAGE);
      return;
    }
    setImportUrlError('');
    if (hasContent({ ...watchedValues, b17_profile: '' })) {
      setPendingImportUrl(url);
      return;
    }
    performB17Import(url, watchedValues);
  };

  const onConfirmB17Overwrite = () => {
    if (pendingImportUrl) {
      performB17Import(pendingImportUrl, watchedValues);
    }
    setPendingImportUrl(null);
  };

  const onCancelB17Overwrite = () => setPendingImportUrl(null);

  const onUndoB17Import = () => {
    if (!importBackup) return;
    reset(importBackup);
    setImportSuggestions(null);
    setImportConfidence({});
    setImportBackup(null);
  };

  const lowConfidenceB17Labels = Object.entries(importConfidence)
    .filter(([, value]) => value < B17_LOW_CONFIDENCE_THRESHOLD)
    .map(([key]) => B17_CONFIDENCE_FIELD_LABELS[key] ?? key);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const onPhotoChange = async ([file]: File[]) => {
    const ext = file.name.split('.').at(-1)?.toLowerCase();
    if (!ext || !ALLOWED_PHOTO_EXTENSIONS.includes(ext)) {
      toast.warn('Загрузите изображение в формате .jpg, .jpeg, .png или .webp');
      return;
    }
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      toast.warn('Размер изображения не должен превышать 5 МБ');
      return;
    }

    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const saved = await uploadPhoto(formData).unwrap();
      setValue('profile_photo', saved.profile_photo, { shouldDirty: true });
      toast.success('Фото профиля обновлено');
    } catch (err: any) {
      const status = err?.status;
      const message =
        status === 413
          ? 'Файл слишком большой (максимум 5 МБ)'
          : status === 400
            ? 'Некорректный файл изображения'
            : status === 502
              ? 'Сервис хранения файлов временно недоступен, попробуйте позже'
              : 'Не удалось загрузить фото';
      toast.error(message);
      setPhotoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
    }
  };
  const photoPicker = useFilePicker({ accept: '.png,.jpg,.jpeg,.webp', onFiles: onPhotoChange });

  const onPhotoDelete = async () => {
    try {
      await deletePhoto().unwrap();
      setPhotoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      setPhotoObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      toast.success('Фото профиля удалено');
    } catch (err: any) {
      const status = err?.status;
      const message =
        status === 502 ? 'Не удалось полностью удалить фото, попробуйте позже' : 'Не удалось удалить фото';
      toast.error(message);
    }
  };

  const onSubmit = async (data: CatalogProfile) => {
    if (!isSaveEnabled) return;
    try {
      const {
        status: _status,
        moderation: _moderation,
        has_published_profile: _hasPublishedProfile,
        diploma_verified: _diplomaVerified,
        ...draft
      } = data;
      await updateProfile(draft).unwrap();
      captureEvent('catalog_profile_draft_saved', {
        edition_kind: data.has_published_profile ? 'revision' : 'new',
      });
      toast.success('Профиль каталога сохранён');
      setImportBackup(null);
      setImportSuggestions(null);
      setImportConfidence({});
    } catch (e: any) {
      const message = e?.data?.message || e?.data?.detail || 'Ошибка сохранения';
      toast.error(message);
    }
  };

  const onSendForReview = async () => {
    await handleSubmit(async (data) => {
      try {
        if (!data.profile_photo) {
          toast.error('Загрузите фото для каталога перед отправкой на публикацию.');
          return;
        }
        const {
          status: _status,
          moderation: _moderation,
          has_published_profile: _hasPublishedProfile,
          diploma_verified: _diplomaVerified,
          ...draft
        } = data;
        await updateProfile(draft).unwrap();
        await submitProfile().unwrap();
        captureEvent('catalog_profile_review_submitted', {
          edition_kind: data.has_published_profile ? 'revision' : 'new',
        });
        toast.success('Анкета отправлена на публикацию');
      } catch (e: any) {
        toast.error(e?.data?.error || 'Не удалось отправить анкету на проверку');
      }
    }, onInvalid)();
  };

  const onWithdraw = async () => {
    try {
      await withdrawProfile().unwrap();
      setValue('status', 'draft');
      toast.success('Заявка возвращена в черновик');
    } catch {
      toast.error('Не удалось отозвать заявку');
    }
  };
  const onUnpublish = async () => {
    try {
      await unpublishProfile().unwrap();
      setValue('status', 'draft');
      toast.success('Анкета снята с публикации');
    } catch {
      toast.error('Не удалось снять анкету с публикации');
    }
  };

  const onInvalid = () => {
    toast.error('Заполните обязательные поля, чтобы опубликовать профиль');
  };

  if (isLoadingMe) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader embedInParent />
      </div>
    );
  }

  const isNewProfile = !meResponse?.exists;
  const photoSrc = photoPreview || photoObjectUrl || photo;

  const formatsItems = dictToMultiItems(dicts?.formats ?? []);
  const issuesItems = dictToMultiItems(dicts?.issues ?? []);
  const languageItems = langToMultiItems(dicts?.languages ?? []);
  const modalitiesItems = dictToMultiItems(dicts?.modalities ?? []);
  const specializationItems = dictToMultiItems(dicts?.specialization ?? []);
  const workingWithItems = dictToMultiItems(dicts?.working_with ?? []);
  const religionItems = dictToMultiItems(dicts?.religions ?? []);
  const educationTypeItems = dictToMultiItems(dicts?.education_types ?? []);

  const genderItems = publishedGender(dicts?.genders ?? []);
  const currencyItems = publishedCurrency(dicts?.currencies ?? []);

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      className="w-full flex flex-col xl:flex-row gap-[40px] lg:gap-[24px]"
    >
      <div className="flex w-full max-w-[330px] flex-col items-center gap-[30px] shrink-0">
        <div className="w-full h-[350px] lg:h-[320px] flex items-center justify-center rounded-card overflow-hidden relative">
          <img src={photoSrc} className="w-full h-full object-cover" alt="" />

          {isUploadingPhoto || isDeletingPhoto ? (
            <div className="w-full h-full absolute inset-0 bg-black/40 flex items-center justify-center">
              <span className="text-white text-heading-md">{isDeletingPhoto ? 'Удаление...' : 'Загрузка...'}</span>
            </div>
          ) : (
            <>
              {photoPicker.input}
              <Pressable
                onClick={photoPicker.open}
                className="absolute inset-0 flex items-center justify-center bg-black/0 text-heading-md text-white underline opacity-0 transition-opacity hover:bg-black/40 hover:opacity-100 focus-visible:bg-black/40 focus-visible:opacity-100"
              >
                Изменить
              </Pressable>
            </>
          )}

          {(photoPreview || photoObjectUrl) && !isUploadingPhoto && !isDeletingPhoto && (
            <CloseButton
              tone="solid"
              label="Удалить фото"
              className="absolute top-2 right-2 z-20"
              onClick={onPhotoDelete}
            />
          )}
        </div>
        {!photoPreview && !photoObjectUrl && (
          <p className="-mt-5 text-center text-ui text-ink-subtle">
            Загрузите отдельное фото для публикации анкеты в каталоге.
          </p>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-6">
        {isNewProfile && (
          <div className="rounded-control border border-warning-line bg-warning-surface px-4 py-3 text-ui text-warning">
            Профиль в каталоге ещё не создан. Заполните форму и сохраните — запись будет создана автоматически. Пока
            статус «Черновик», анкета скрыта от публики.
          </div>
        )}

        <Card variant="raised" padding="lg">
          <SectionTitle>Статус профиля</SectionTitle>
          <div className="flex gap-3 rounded-control border border-line bg-surface-muted px-4 py-3" aria-live="polite">
            <span
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-card ring-1 ring-inset ${statusMeta.iconClass}`}
              aria-hidden="true"
            >
              <span
                className="material-symbols-rounded block text-display-s font-normal leading-none"
                style={{ fontVariationSettings: "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 24" }}
              >
                {statusMeta.icon}
              </span>
            </span>
            <div className="min-w-0">
              <p className="font-medium text-ink">{statusMeta.title}</p>
              <p className="mt-1 text-ui text-ink-muted">{statusMessage}</p>
              {publicProfileUrl && (
                <TextLink
                  href={publicProfileUrl}
                  target="_blank"
                  tone="danger"
                  underline="hover"
                  className="mt-3 inline-flex items-center gap-1.5 text-ui-strong"
                >
                  Открыть профиль в каталоге
                  <span className="material-symbols-rounded text-body-xl leading-none" aria-hidden="true">
                    open_in_new
                  </span>
                </TextLink>
              )}
            </div>
          </div>
        </Card>

        <Card variant="raised" padding="lg">
          <SectionTitle>Основная информация</SectionTitle>
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Controller
                name="last_name"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Фамилия"
                    size="lg"
                    placeholder="Введите фамилию"
                    value={field.value || ''}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="first_name"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Имя"
                    size="lg"
                    placeholder="Введите имя"
                    value={field.value || ''}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Controller
                name="middle_name"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Отчество"
                    size="lg"
                    placeholder="Введите отчество"
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                name="age"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Возраст"
                    size="lg"
                    type="number"
                    min={0}
                    placeholder="Введите возраст"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Controller
                name="city"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Город"
                    size="lg"
                    placeholder="Введите город"
                    value={field.value || ''}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <div className="w-full">
                    <CustomSelect
                      size="lg"
                      label="Пол"
                      items={genderItems.map((g) => g.gender)}
                      placeholder="Выберите пол"
                      selected={genderItems.find((g) => g.id === field.value)?.gender ?? ''}
                      onChange={(name) => field.onChange(genderItems.find((g) => g.gender === name)?.id ?? '')}
                      isChange={false}
                    />
                  </div>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Controller
                name="title"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Заголовок"
                    size="lg"
                    placeholder="Например: Клинический психолог"
                    value={field.value || ''}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="experience_years"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Стаж (лет)"
                    size="lg"
                    type="number"
                    min={0}
                    placeholder="Введите стаж"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))}
                  />
                )}
              />
            </div>

            <Controller
              name="specialization"
              control={control}
              render={({ field, fieldState }) => (
                <MultiSelect
                  size="lg"
                  label="Специализация"
                  items={specializationItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите специализацию"
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        </Card>

        <Card variant="raised" padding="lg">
          <SectionTitle>Консультации</SectionTitle>
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Controller
                name="price"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    label="Стоимость онлайн"
                    size="lg"
                    type="number"
                    min={0}
                    placeholder="0"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="price_offline"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Стоимость оффлайн"
                    size="lg"
                    type="number"
                    min={0}
                    placeholder="0"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))}
                  />
                )}
              />
              <Controller
                name="currency"
                control={control}
                render={({ field, fieldState }) => {
                  const selectedCurrency = currencyItems.find((c) => c.id === field.value);
                  return (
                    <div className="w-full">
                      <CustomSelect
                        size="lg"
                        label="Валюта"
                        items={currencyItems.map(currencyLabel)}
                        placeholder="Выберите валюту"
                        selected={selectedCurrency ? currencyLabel(selectedCurrency) : ''}
                        onChange={(label) => {
                          const found = currencyItems.find((c) => currencyLabel(c) === label);
                          field.onChange(found?.id ?? '');
                        }}
                        isChange={false}
                        error={fieldState.error?.message}
                      />
                    </div>
                  );
                }}
              />
              <Controller
                name="duration_minutes"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Длительность (мин)"
                    size="lg"
                    type="number"
                    min={0}
                    placeholder="60"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))}
                  />
                )}
              />
            </div>

            <Controller
              name="formats"
              control={control}
              render={({ field, fieldState }) => (
                <MultiSelect
                  size="lg"
                  label="Форматы консультаций"
                  items={formatsItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите форматы"
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        </Card>

        <Card variant="raised" padding="lg">
          <SectionTitle>Образование</SectionTitle>
          <div className="space-y-5">
            <Controller
              name="education_description"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  label="Образование"
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Опишите ваше образование"
                />
              )}
            />
            <Controller
              name="education_type"
              control={control}
              render={({ field }) => (
                <div className="w-full">
                  <CustomSelect
                    size="lg"
                    label="Тип образования"
                    items={educationTypeItems.map((i) => i.name)}
                    placeholder="Выберите тип образования"
                    selected={educationTypeItems.find((i) => i.id === field.value)?.name ?? ''}
                    onChange={(name) => field.onChange(educationTypeItems.find((i) => i.name === name)?.id ?? '')}
                    isChange={false}
                  />
                </div>
              )}
            />
          </div>
        </Card>

        <Card variant="raised" padding="lg">
          <SectionTitle>О специалисте</SectionTitle>
          <div className="space-y-5">
            <Controller
              name="about_me"
              control={control}
              render={({ field, fieldState }) => (
                <RichTextEditor
                  label="Обо мне"
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Расскажите о себе"
                  minHeight={200}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="languages"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  size="lg"
                  label="Языки"
                  items={languageItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите языки"
                />
              )}
            />
            <Controller
              name="religion"
              control={control}
              render={({ field }) => (
                <div className="w-full">
                  <CustomSelect
                    size="lg"
                    label="Религия"
                    items={religionItems.map((i) => i.name)}
                    placeholder="Выберите религию"
                    selected={religionItems.find((i) => i.id === field.value)?.name ?? ''}
                    onChange={(name) => field.onChange(religionItems.find((i) => i.name === name)?.id ?? '')}
                    isChange={false}
                  />
                </div>
              )}
            />
            <Controller
              name="modalities"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  size="lg"
                  label="Методы лечения"
                  items={modalitiesItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите методы лечения"
                />
              )}
            />
            <Controller
              name="issues"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  size="lg"
                  label="Запросы"
                  items={issuesItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите запросы клиентов"
                />
              )}
            />
            <Controller
              name="working_with"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  size="lg"
                  label="С кем работаю"
                  items={workingWithItems}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Выберите категории клиентов"
                />
              )}
            />
          </div>
        </Card>

        <Card variant="raised" padding="lg">
          <SectionTitle>Контакты</SectionTitle>
          {contactError && (
            <p className="mb-4 text-ui text-primary" role="alert">
              {contactError}
            </p>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Controller
              name="whatsup"
              control={control}
              render={({ field }) => (
                <PhoneInputWithMask
                  classNameWrap="md:min-w-0"
                  size="lg"
                  label="WhatsApp"
                  selected={field.value || ''}
                  onChange={field.onChange}
                  error={contactError}
                  showError={false}
                />
              )}
            />
            <Controller
              name="telegram"
              control={control}
              render={({ field }) => (
                <Input
                  label="Telegram"
                  size="lg"
                  placeholder="@username"
                  value={field.value || ''}
                  onChange={field.onChange}
                  error={contactError}
                  showError={false}
                />
              )}
            />
            <Controller
              name="phone_1"
              control={control}
              render={({ field }) => (
                <PhoneInputWithMask
                  classNameWrap="md:min-w-0"
                  size="lg"
                  label="Телефон 1"
                  selected={field.value || ''}
                  onChange={field.onChange}
                  error={contactError}
                  showError={false}
                />
              )}
            />
            <Controller
              name="phone_2"
              control={control}
              render={({ field }) => (
                <PhoneInputWithMask
                  classNameWrap="md:min-w-0"
                  size="lg"
                  label="Телефон 2"
                  selected={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="vk"
              control={control}
              render={({ field }) => (
                <Input
                  label="VK"
                  size="lg"
                  placeholder="vk.com/..."
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </Card>

        <Card variant="raised" padding="lg">
          <SectionTitle>Другие платформы</SectionTitle>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <div className="min-w-0 flex-1">
                <Controller
                  name="b17_profile"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="B17"
                      size="lg"
                      placeholder="Ссылка на профиль b17.ru"
                      value={field.value || ''}
                      onChange={(e) => {
                        field.onChange(e);
                        if (importUrlError) setImportUrlError('');
                      }}
                      error={importUrlError}
                    />
                  )}
                />
              </div>
              <div className="shrink-0">
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  loading={isImportingB17}
                  className="w-full sm:w-auto"
                  onClick={onImportB17Click}
                >
                  Заполнить из b17
                </Button>
              </div>
            </div>

            {importBackup && (
              <div className="flex flex-col gap-2 rounded-control border border-info-line bg-info-surface px-4 py-3 text-ui text-info sm:flex-row sm:items-center sm:justify-between">
                <span>
                  Поля предзаполнены данными из b17.ru. Проверьте их и сохраните вручную либо отмените предзаполнение.
                </span>
                <TextButton size="md" tone="primary" className="shrink-0" onClick={onUndoB17Import}>
                  Отменить предзаполнение
                </TextButton>
              </div>
            )}

            {lowConfidenceB17Labels.length > 0 && (
              <div className="rounded-control border border-warning-line bg-warning-surface px-4 py-3 text-ui text-warning">
                Низкая уверенность распознавания: {lowConfidenceB17Labels.join(', ')}. Проверьте эти поля перед
                сохранением.
              </div>
            )}

            {importSuggestions &&
              B17_IMPORT_M2M_FIELDS.map(([sourceKey]) => {
                const list = (importSuggestions[sourceKey] ?? []).filter((s) => !s.id || s.unmatched);
                if (!list.length) return null;
                return (
                  <div
                    key={sourceKey}
                    className="rounded-control border border-line bg-surface-muted px-4 py-3 text-ui text-ink-muted"
                  >
                    <p className="font-medium text-ink">
                      Не удалось однозначно сопоставить «{B17_IMPORT_SECTION_LABELS[sourceKey]}»:
                    </p>
                    <ul className="mt-1 list-disc space-y-0.5 pl-5">
                      {list.map((s, i) => (
                        <li key={i}>
                          {s.unmatched || s.title || '—'}
                          {s.source_text && <span className="text-ink-subtle"> (из анкеты: «{s.source_text}»)</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}

            <div className="flex flex-wrap gap-6 pt-1">
              {(
                [
                  ['work_on_yasno', 'Yasno'],
                  ['work_on_alter', 'Alter'],
                  ['work_on_meta', 'Meta'],
                  ['work_on_zigmung', 'Zigmung'],
                ] as const
              ).map(([name, label]) => (
                <Controller
                  key={name}
                  name={name}
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      size="sm"
                      label={label}
                      checked={field.value as boolean}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              ))}
            </div>
          </div>
        </Card>

        <div className="pb-2">
          <div className="flex flex-col gap-3 sm:flex-row">
            {watchedValues.status !== 'ready_for_review' && watchedValues.status !== 'manual_review' && (
              <Button
                type="submit"
                size="lg"
                variant="outline"
                className="flex-1"
                disabled={!isSaveEnabled}
                loading={isSaving}
              >
                Сохранить черновик
              </Button>
            )}
            {['draft', 'rejected', 'published'].includes(watchedValues.status) && (
              <Button
                type="button"
                size="lg"
                className="flex-1"
                disabled={isSaving || !canSendForReview}
                loading={isSubmitting}
                onClick={onSendForReview}
              >
                {watchedValues.status === 'published' ? 'Отправить изменения на публикацию' : 'Отправить на публикацию'}
              </Button>
            )}
            {['ready_for_review', 'manual_review', 'rejected'].includes(watchedValues.status) && (
              <Button
                type="button"
                size="lg"
                variant="neutral"
                className="flex-1"
                loading={isWithdrawing}
                onClick={onWithdraw}
              >
                Отозвать заявку
              </Button>
            )}
            {hasPublishedProfile && (
              <Button type="button" size="lg" variant="neutral" loading={isUnpublishing} onClick={onUnpublish}>
                Снять с публикации
              </Button>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!pendingImportUrl}
        title="Перезаписать заполненные поля?"
        message="В форме уже есть данные. Импорт из b17.ru заменит совпадающие поля значениями из анкеты b17.ru."
        confirmText="Перезаписать"
        onConfirm={onConfirmB17Overwrite}
        onCancel={onCancelB17Overwrite}
      />
    </form>
  );
};
